import { css } from '@emotion/react'
import type {
  ActiveElement,
  ChartData as ChartJsData,
  ChartOptions,
  ScriptableContext,
  TooltipPositionerFunction,
} from 'chart.js'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import { useMemo, useState } from 'react'
import { Chart } from 'react-chartjs-2'

ChartJS.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
)

// canvas는 CSS var()를 지원하지 않아 getComputedStyle로 실제 hex를 꺼냄
const resolveCssVar = (name: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

const CSS_VAR_MAP = {
  barActive: '--Colors-Blue-600',
  barHover: '--Colors-Blue-600',
  barInactive: '--Colors-Blue-50',
  line: '--Colors-Blue-400',
  label: '--Colors-Gray-800',
} as const

const BAR_MAX_WIDTH = 36
const TOOLTIP_GAP = 20
const AXIS_TOP_PADDING_RATIO = 0.08
const AXIS_MIN_PADDING = 8
const CHART_LAYOUT_PADDING = {
  top: 8,
  bottom: 8,
} as const

// 커스텀 툴팁 positioner: 라인 포인트(점)의 정확한 좌표를 앵커로 사용
// 내장 positioner('average')는 바+라인의 중간 높이를 반환해서
// 점 바로 위/아래에 툴팁을 띄우려면 라인 포인트 좌표가 필요
declare module 'chart.js' {
  interface TooltipPositionerMap {
    pointCenter: TooltipPositionerFunction<'bar' | 'line'>
  }
}

if (!Tooltip.positioners.pointCenter) {
  Tooltip.positioners.pointCenter = (
    elements: readonly ActiveElement[],
  ): { x: number; y: number } | false => {
    if (!elements.length) return false
    // datasetIndex 1 = 라인 데이터셋, 없으면 첫 번째(바)로 폴백
    const anchor = elements.find((el) => el.datasetIndex === 1) ?? elements[0]
    const pos = anchor.element.tooltipPosition(false)
    return { x: pos.x, y: pos.y }
  }
}

// 툴팁 DOM 구조를 한 번만 생성하고, 이후엔 텍스트만 업데이트
const getOrCreateTooltip = (chart: ChartJS) => {
  const parent = chart.canvas.parentElement!
  let root = parent.querySelector<HTMLDivElement>('.chart-tooltip')

  if (root) {
    return {
      root,
      title: root.querySelector<HTMLParagraphElement>('.tt-title')!,
      barValue: root.querySelector<HTMLSpanElement>('.tt-bar-val')!,
      lineValue: root.querySelector<HTMLSpanElement>('.tt-line-val')!,
    }
  }

  root = document.createElement('div')
  root.className = 'chart-tooltip'
  root.innerHTML = `
    <p class="tt-title"></p>
    <div class="tt-body">
      <div class="tt-item">
        <span class="tt-ind tt-bar"></span>
        <span class="tt-bar-val"></span>
      </div>
      <div class="tt-item">
        <span class="tt-ind tt-dot"></span>
        <span class="tt-line-val"></span>
      </div>
    </div>
  `
  parent.appendChild(root)

  return {
    root,
    title: root.querySelector<HTMLParagraphElement>('.tt-title')!,
    barValue: root.querySelector<HTMLSpanElement>('.tt-bar-val')!,
    lineValue: root.querySelector<HTMLSpanElement>('.tt-line-val')!,
  }
}

export type BarLineChartItem = {
  label: string // X축 라벨
  barValue: number // 막대 값
  lineValue: number // 라인(점) 값
  tooltipTitle?: string // 툴팁 제목 (ex: "2026.04.13~04.19")
  id: string
}

/**
 * 바+라인 혼합 차트 (공통 컴포넌트)
 * - 막대 클릭 → 해당 막대 진한 파랑으로 선택 토글
 * - 호버 → 커스텀 HTML 툴팁이 바 상단에 표시
 */
const BarLineChart = ({
  data,
  barLabel = '문제수',
  barUnit = '문제',
  lineLabel = '정답률',
  lineUnit = '%',
  lineMax = 100,
  barMaxWidth = BAR_MAX_WIDTH,
}: {
  data: BarLineChartItem[]
  barLabel?: string // 바 범례 라벨
  barUnit?: string // 바 값 단위
  lineLabel?: string // 라인 범례 라벨
  lineUnit?: string // 라인 값 단위
  lineMax?: number // 라인 y축 최대값
  barMaxWidth?: number // 막대 최대 폭
}) => {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)

  const colors = useMemo(
    () => ({
      barActive: resolveCssVar(CSS_VAR_MAP.barActive),
      barInactive: resolveCssVar(CSS_VAR_MAP.barInactive),
      barHover: resolveCssVar(CSS_VAR_MAP.barHover),
      line: resolveCssVar(CSS_VAR_MAP.line),
      label: resolveCssVar(CSS_VAR_MAP.label),
    }),
    [],
  )

  const maxBarValue = useMemo(() => {
    const max = Math.max(...data.map((d) => d.barValue), 0)
    return max <= 10 ? 10 : max
  }, [data])

  const countAxisPadding = useMemo(() => {
    return Math.max(maxBarValue * AXIS_TOP_PADDING_RATIO, AXIS_MIN_PADDING)
  }, [maxBarValue])

  const percentageAxisPadding = useMemo(() => {
    return Math.max(lineMax * AXIS_TOP_PADDING_RATIO, AXIS_MIN_PADDING)
  }, [lineMax])

  const countAxisBottomPadding = useMemo(() => {
    const yPctRange = lineMax + percentageAxisPadding * 2
    const baselineRatio = percentageAxisPadding / yPctRange
    return (baselineRatio * (maxBarValue + countAxisPadding)) / (1 - baselineRatio)
  }, [lineMax, percentageAxisPadding, maxBarValue, countAxisPadding])

  const chartData = useMemo<ChartJsData<'bar' | 'line', number[], string>>(
    () => ({
      labels: data.map((d) => d.label),
      datasets: [
        {
          type: 'bar' as const,
          label: barLabel,
          data: data.map((d) => d.barValue),
          yAxisID: 'yCount',
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: barMaxWidth,
          backgroundColor: (ctx: ScriptableContext<'bar'>) =>
            ctx.dataIndex === selectedIdx ? colors.barActive : colors.barInactive,
          hoverBackgroundColor: colors.barHover,
          pointStyle: 'rectRounded',
          order: 2,
        },
        {
          type: 'line' as const,
          label: lineLabel,
          data: data.map((d) => d.lineValue),
          yAxisID: 'yPct',
          borderColor: colors.line,
          backgroundColor: colors.line,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 4,
          pointBackgroundColor: colors.line,
          pointBorderColor: colors.line,
          pointStyle: 'circle',
          tension: 0,
          order: 1,
        },
      ],
    }),
    [data, selectedIdx, barLabel, lineLabel, colors, barMaxWidth],
  )

  const options = useMemo<ChartOptions<'bar' | 'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      devicePixelRatio: window.devicePixelRatio,
      animation: {
        duration: 500,
        easing: 'easeOutCubic',
      },
      animations: {
        x: { duration: 0 },
      },
      layout: {
        padding: CHART_LAYOUT_PADDING,
      },
      onClick: (_evt, elements) => {
        if (elements.length === 0) return
        const idx = elements[0].index
        setSelectedIdx((prev) => (prev === idx ? null : idx))
      },
      onHover: (_evt, elements, chart) => {
        ;(chart.canvas as HTMLCanvasElement).style.cursor =
          elements.length > 0 ? 'pointer' : 'default'
      },
      plugins: {
        tooltip: {
          enabled: false,
          position: 'pointCenter',
          mode: 'index',
          intersect: false,
          external: ({ chart, tooltip: model }) => {
            const tt = getOrCreateTooltip(chart)
            if (model.opacity === 0) {
              tt.root.style.opacity = '0'
              return
            }
            if (!model.dataPoints?.length) return
            const idx = model.dataPoints[0].dataIndex
            const d = data[idx]
            const barVal = model.dataPoints.find((p) => p.datasetIndex === 0)?.parsed.y ?? 0
            const lineVal = model.dataPoints.find((p) => p.datasetIndex === 1)?.parsed.y ?? 0
            const tooltipTitle: string = d?.tooltipTitle?.trim() ?? ''
            tt.title.textContent = tooltipTitle
            tt.title.style.display = tooltipTitle ? 'block' : 'none'
            tt.barValue.innerHTML = `${barLabel} <strong>${barVal}${barUnit}</strong>`
            tt.lineValue.innerHTML = `${lineLabel} <strong>${lineVal}${lineUnit}</strong>`
            // 툴팁 크기 측정
            tt.root.style.opacity = '1'
            tt.root.style.transform = 'none'
            const ttW = tt.root.offsetWidth
            const ttH = tt.root.offsetHeight
            // 좌우: 오른쪽에 공간 부족하면 왼쪽에 표시
            const showLeft = model.caretX + ttW > chart.width
            let x = showLeft ? model.caretX - ttW : model.caretX
            // 상하: 위에 공간 부족하면 아래에 표시
            const showBelow = model.caretY - ttH - TOOLTIP_GAP < 0
            const y = showBelow ? model.caretY + TOOLTIP_GAP : model.caretY - ttH - TOOLTIP_GAP
            x = Math.max(0, Math.min(x, chart.width - ttW))
            tt.root.style.left = `${x}px`
            tt.root.style.top = `${y}px`
            // border-radius: TL TR BR BL — 꼬리(점 방향) 꼭짓점만 0
            if (showBelow && showLeft) {
              tt.root.style.borderRadius = '8px 0 8px 8px' // 점이 우상 → TR=0
            } else if (showBelow) {
              tt.root.style.borderRadius = '0 8px 8px 8px' // 점이 좌상 → TL=0
            } else if (showLeft) {
              tt.root.style.borderRadius = '8px 8px 0 8px' // 점이 우하 → BR=0
            } else {
              tt.root.style.borderRadius = '8px 8px 8px 0' // 점이 좌하 → BL=0
            }
          },
        },
      },
      scales: {
        x: {
          grid: { display: false, drawTicks: false, drawBorder: false },
          ticks: {
            color: colors.label,
            font: { size: 14, family: 'Pretendard' },
            padding: 10,
          },
        },
        yCount: {
          type: 'linear',
          display: false,
          min: -countAxisBottomPadding,
          max: maxBarValue + countAxisPadding,
          position: 'left',
        },
        yPct: {
          type: 'linear',
          display: false,
          min: -percentageAxisPadding,
          max: lineMax + percentageAxisPadding,
          position: 'right',
        },
      },
    }),
    [
      data,
      maxBarValue,
      countAxisPadding,
      countAxisBottomPadding,
      percentageAxisPadding,
      barLabel,
      barUnit,
      lineLabel,
      lineUnit,
      lineMax,
      colors,
    ],
  )

  return (
    <div css={_Style}>
      <Chart type="bar" data={chartData} options={options} />
    </div>
  )
}

export default BarLineChart

const _Style = css`
  position: relative;
  overflow: visible;
  width: 100%;
  height: 100%;

  canvas {
    cursor: pointer;
  }

  .chart-tooltip {
    position: absolute;
    pointer-events: none;
    background: color-mix(in srgb, var(--Colors-Gray-800) 80%, transparent);
    padding: 10px;
    box-shadow:
      0px 3px 16px 0px rgba(41, 42, 43, 0.05),
      0px 4px 8px 0px rgba(41, 42, 43, 0.08);
    transition:
      opacity 0.2s ease,
      left 0.2s ease,
      top 0.2s ease;
    opacity: 0;
    z-index: 10;
    white-space: nowrap;
  }

  .tt-title {
    font-size: 9px;
    line-height: 12px;
    color: var(--White);
    margin: 0 0 4px;
  }

  .tt-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .tt-item {
    display: flex;
    align-items: center;
    font-size: 12px;
    line-height: 18px;
    color: white;
  }

  .tt-item strong {
    font-weight: 700;
  }

  .tt-ind {
    flex-shrink: 0;
    margin-right: 4px;
  }

  .tt-bar {
    width: 4px;
    height: 6px;
    border-radius: 1px 1px 0 0;
    background: var(--Colors-Blue-600);
  }

  .tt-dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--Colors-Blue-400);
  }
`
